import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthStateFacade } from '@zero/shared/util-global-state';
import { MultiExanteResponse, SavingsPlan, SavingsPlansOverview, TopInstrument } from '@zero/shared/util-models';
import { AssetSearchService } from '@zero/shared/util-services';
import { environment } from 'environments/environment';
import { Observable, map, of } from 'rxjs';
import { MultiSavingsPlan } from '../entities/multi-savings-plan.entity';
import { SavingsPlansOptionsResponse } from '../entities/options.entity';
import { SavingsPlanSubject } from '../entities/saving-plan-search.mode';
import { SavingsPlanEdit } from '../entities/savings-plan-edit.entity';

@Injectable({ providedIn: 'root' })
export class SavingsPlanInfrastructure {
  readonly #endpoint = environment.apihost + environment.savingsplanApi;

  readonly #http = inject(HttpClient);
  readonly #assetSearchService = inject(AssetSearchService);
  readonly #authStateFacade = inject(AuthStateFacade);

  public getSavingsPlansOverview() {
    return this.#http.get<SavingsPlansOverview>(`${this.#endpoint}/overview`, {
      params: { customerId: this.#authStateFacade.customerId },
    });
  }

  public deleteSavingsPlan(plan: SavingsPlan) {
    return this.#http.delete<void>(this.#endpoint, { body: { ...plan } });
  }

  public getExecDates() {
    return this.#http.get<string[]>(`${this.#endpoint}/execDates`);
  }

  public getMostChosen(itemsCount: number) {
    return this.#http.get<TopInstrument[]>(`${environment.apihost}/api/stats/savingplans/top/${itemsCount}`);
  }

  public getSubjects() {
    return this.#http.get<SavingsPlanSubject[]>(`${environment.apihost}/assets/instruments/recommended-instruments.json`);
  }

  public createSavingsPlans(savingsPlans: MultiSavingsPlan[], execDate: string, interval: string, dynamization: number) {
    return this.#http.post<SavingsPlan[]>(`${this.#endpoint}/multi`, {
      customerId: this.#authStateFacade.customerId,
      interval,
      savingsPlansToCreate: savingsPlans.map(({ isin, amount }) => ({
        isin,
        firstExecutionDate: new Date(execDate),
        amount,
        dynamization: dynamization || 0,
      })),
    });
  }

  public updateSavingsPlan(savingsPlan: SavingsPlan, execDate: string, interval: string, dynamization: number) {
    const { id, amount, isin } = savingsPlan;

    return this.#http.put<void>(`${this.#endpoint}`, {
      customerId: this.#authStateFacade.customerId,
      id,
      amount,
      isin,
      nextExecDate: new Date(execDate).toJSON(),
      interval,
      dynamization: dynamization || 0,
    });
  }

  public getOptions() {
    return this.#http.get<SavingsPlansOptionsResponse>(`${this.#endpoint}/options`);
  }

  public loadExante(isins: string[]) {
    return this.#http.post<MultiExanteResponse>(`${environment.apihost}/apic/exante/multi`, {
      customerId: this.#authStateFacade.customerId,
      isins,
      sparplan: true,
    });
  }

  public loadExistingSavingsPlan(plan: SavingsPlan): Observable<SavingsPlanEdit> {
    if (!plan?.isin) {
      return of({ asset: null, plan });
    }
    return this.#assetSearchService.loadAssetByIsin(plan.isin).pipe(map((asset) => ({ asset, plan })));
  }

  public submitOnboardingSavingsPlan(amount: number, isin: string) {
    return this.#http.post(`${this.#endpoint}/preliminary`, { savingsPlansToCreate: [{ isin, amount }] });
  }

  public loadOnboardingSavingsPlans() {
    return this.#http.get<SavingsPlan[]>(`${this.#endpoint}/preliminary`);
  }

  public deleteOnboardingSavingsPlan(id: string) {
    return this.#http.delete<void>(`${this.#endpoint}/preliminary/${id}`);
  }
}
